import { type ReactNode } from "react";
import { useLocation } from "wouter";
import { useMemberAuth } from "@/hooks/use-member-auth";
import { Button } from "@/components/ui/button";
import { Home, MessageSquare, Wrench, LogOut, Sparkles } from "lucide-react";

// Shared chrome for the member portal: a compact brand header and a fixed
// bottom tab bar (mobile-first — most residents are on a phone). Mirrors the
// owner/partner shells but trimmed to the two member surfaces.
export function MemberLayout({ title, children }: { title: string; children: ReactNode }) {
  const [location, setLocation] = useLocation();
  const { member, logout } = useMemberAuth();

  const tabs = [
    { key: "dashboard", label: "Home", icon: Home, path: "/member/dashboard" },
    { key: "messages", label: "Messages", icon: MessageSquare, path: "/member/messages" },
    { key: "cleaning", label: "Cleaning", icon: Sparkles, path: "/member/cleaning" },
    { key: "maintenance", label: "Maintenance", icon: Wrench, path: "/member/maintenance" },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="sticky top-0 z-10 flex items-center justify-between border-b bg-brand px-4 py-3 text-brand-foreground">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold leading-tight">{title}</p>
          {member && <p className="truncate text-xs opacity-80">{member.name}</p>}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-brand-foreground hover:bg-brand-hover hover:text-brand-foreground"
          onClick={async () => {
            await logout();
            setLocation("/member/login");
          }}
        >
          <LogOut className="h-4 w-4" />
          <span className="sr-only">Log out</span>
        </Button>
      </header>

      <main className="mx-auto w-full max-w-2xl flex-1 px-4 pb-24 pt-4">{children}</main>

      <nav className="fixed inset-x-0 bottom-0 z-10 mx-auto flex max-w-2xl border-t bg-card">
        {tabs.map((t) => {
          const active = location.startsWith(t.path);
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setLocation(t.path)}
              className={`flex flex-1 flex-col items-center gap-0.5 py-2.5 text-xs ${
                active ? "text-brand" : "text-muted-foreground"
              }`}
            >
              <Icon className="h-5 w-5" />
              {t.label}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
